import { useCallback, useEffect, useState } from 'react';
import { useAppearanceDelay } from 'hooks';
import { AiService } from 'types';
import { message } from 'components/ui';

export type PromoPhase = { alert: string | null; text: string | null } & (
  | { status: 'checking' }
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'submitted' }
  | { status: 'check-error' }
);

type Props<T extends AiService> = {
  open: boolean;
  apiService: T;
};

export const useAiPromo = <T extends AiService>({
  open,
  apiService,
}: Props<T>) => {
  const [phase, setPhase] = useState<PromoPhase>({
    status: 'checking',
    alert: null,
    text: null,
  });

  const check = useCallback(async () => {
    setPhase((prev) => ({ ...prev, status: 'checking' }));

    try {
      const response = await apiService.CheckAiTicketStatus();
      setPhase(
        response.alert
          ? { status: 'submitted', ...response }
          : { status: 'idle', ...response }
      );
    } catch {
      setPhase((prev) => ({ ...prev, status: 'check-error' }));
    }
  }, []);

  useEffect(() => {
    if (open) {
      check();
    }
  }, [open, check]);

  const submit = useCallback(async () => {
    setPhase((prev) => ({ ...prev, status: 'submitting' }));

    try {
      await apiService.CreateAiTicket();
      setPhase((prev) => ({
        ...prev,
        status: 'submitted',
      }));

      return true;
    } catch {
      setPhase((prev) => ({ ...prev, status: 'idle' }));
      message({ type: 'error' });

      return false;
    }
  }, []);

  const isDelayedChecking = useAppearanceDelay(phase.status === 'checking', {
    delayMs: 0,
  });

  const displayPhase: PromoPhase =
    isDelayedChecking || phase.status === 'checking'
      ? { ...phase, status: 'checking' }
      : phase;

  return { phase: displayPhase, submit, check };
};
